class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
    this.setupGenderFilter();
    this.cleanVisibleInputFromUrl();
    this.applyClientSideGenderRefinement();
    // Run after the DOM is fully ready so facet inputs exist.
    window.setTimeout(() => this.autoApplyColorFacetFromQuery(), 0);
  }

  setupEventListeners() {
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (MainSearch._globalSyncBound) return;
    MainSearch._globalSyncBound = true;

    document.addEventListener('input', (event) => {
      const target = event.target;
      if (!target || !target.matches || !target.matches('input[type="search"][name="q"]')) return;
      if (MainSearch._syncing) return;
      MainSearch._syncing = true;
      try {
        document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
          if (input === target) return;
          if (document.activeElement === input) return;
          if (input.value === target.value) return;
          input.value = target.value;
        });
      } finally {
        MainSearch._syncing = false;
      }
    });
  }

  setupGenderFilter() {
    // Add gender filter functionality
    const genderFilterContainer = document.querySelector('.gender-filter-container');
    if (!genderFilterContainer) return;

    const genderButtons = genderFilterContainer.querySelectorAll('.gender-filter-btn');
    const searchForm = document.querySelector('form[action*="/search"]');
    
    if (!searchForm) return;

    // Initialize active state based on current URL/query
    this.initializeGenderFilterState(genderButtons);

    genderButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove active class from all buttons
        genderButtons.forEach(btn => btn.classList.remove('active'));
        
        // Add active class to clicked button
        button.classList.add('active');
        
        // Get the gender value
        const gender = button.dataset.gender;
        
        // Update URL with gender filter
        this.updateSearchWithGenderFilter(gender);
      });
    });
  }

  initializeGenderFilterState(genderButtons) {
    const currentUrl = new URL(window.location);
    // Explicit gender param takes precedence
    const genderParam = (currentUrl.searchParams.get('gender') || '').toLowerCase();
    const qParam = currentUrl.searchParams.get('q') || '';

    // Detect synonyms in query
    const menSynonyms = [
      'men', 'man', 'male', 'mens', "men's", 'boy', 'boys'
    ];
    const womenSynonyms = [
      'women', 'woman', 'female', 'womens', "women's", 'lady', 'ladies', 'girl', 'girls'
    ];

    const hasMenTag = new RegExp(`(^|\\s)tag:\\"?(?:${menSynonyms.join('|')})\\"?(?=\\s|$)`, 'i').test(qParam);
    const hasWomenTag = new RegExp(`(^|\\s)tag:\\"?(?:${womenSynonyms.join('|')})\\"?(?=\\s|$)`, 'i').test(qParam);

    // Remove active class from all buttons
    genderButtons.forEach(btn => btn.classList.remove('active'));
    
    // Set active class based on current query
    let gender = 'all';
    if (genderParam === 'men' || genderParam === 'women') {
      gender = genderParam;
    } else {
      if (hasMenTag && !hasWomenTag) gender = 'men';
      if (hasWomenTag && !hasMenTag) gender = 'women';
    }
    const activeButton = Array.from(genderButtons).find(btn => btn.dataset.gender === gender);
    if (activeButton) activeButton.classList.add('active');
  }

  updateSearchWithGenderFilter(gender) {
    const currentUrl = new URL(window.location);
    const searchParams = currentUrl.searchParams;

    // Start from current q/input value
    const inputValue = document.querySelector('input[name="q"]')?.value || searchParams.get('q') || '';

    // Remove any existing gender tag filters from q
    let baseQuery = inputValue
      .replace(/\s*AND\s*tag:\"?(?:men|man|male|mens|women|woman|female|womens|ladies|girl|girls|boy|boys)\"?/ig, '')
      .replace(/(^|\s)tag:\"?(?:men|man|male|mens|women|woman|female|womens|ladies|girl|girls|boy|boys)\"?(?=\s|$)/ig, '')
      .trim();

    // Build synonym OR groups for Shopify search including common admin tagging styles
    const menTokens = [
      'Men', 'Man', 'Male', 'Mens', 'Boy', 'Boys',
      'Gender: Men', 'Gender: Male', 'Target Gender: Men', 'Target Gender: Male', 'gender:men', 'gender:male'
    ];
    const womenTokens = [
      'Women', 'Woman', 'Female', 'Womens', 'Ladies', 'Lady', 'Girl', 'Girls',
      'Gender: Women', 'Gender: Female', 'Target Gender: Women', 'Target Gender: Female', 'gender:women', 'gender:female'
    ];
    const menGroup = `(${menTokens.map(t => `tag:"${t}"`).join(' OR ')})`;
    const womenGroup = `(${womenTokens.map(t => `tag:"${t}"`).join(' OR ')})`;

    // Append gender tag if selected
    let newQuery = baseQuery;
    if (gender === 'men') {
      newQuery = baseQuery ? `${baseQuery} AND ${menGroup}` : menGroup;
    } else if (gender === 'women') {
      newQuery = baseQuery ? `${baseQuery} AND ${womenGroup}` : womenGroup;
    }

    // Ensure we search products only
    searchParams.set('type', 'product');
    // Use strict word matching to avoid prefix/plural equivalence.
    searchParams.set('options[prefix]', 'none');
    searchParams.set('q', newQuery);
    // Persist explicit gender param so UI can pick it up
    if (gender === 'all') {
      searchParams.delete('gender');
    } else {
      searchParams.set('gender', gender);
    }

    // Navigate to updated URL
    window.location.href = `${currentUrl.pathname}?${searchParams.toString()}`;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    if (MainSearch._syncing) return;
    MainSearch._syncing = true;
    try {
      document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
        if (input === target) return;
        if (document.activeElement === input) return;
        if (input.value === value) return;
        input.value = value;
      });
    } finally {
      MainSearch._syncing = false;
    }
  }

  // Remove tag filters from the visible input so the query looks clean to users
  cleanVisibleInputFromUrl() {
    try {
      const url = new URL(window.location.href);
      const typed = url.searchParams.get('oq');
      const q = typed || url.searchParams.getAll('q').join(' ') || url.searchParams.get('q') || '';
      let base = this.stripGenderTagGroups(q);
      const parts = String(base || '').trim().split(/\s+/);
      if (parts.length >= 2 && parts.length % 2 === 0) {
        const left = parts.slice(0, parts.length / 2).join(' ');
        const right = parts.slice(parts.length / 2).join(' ');
        if (left && left === right) base = left;
      }
      if (base !== undefined) {
        this.allSearchInputs.forEach((input) => {
          input.value = base.trim();
        });
      }
    } catch (_) {}
  }

  // Strip gender tag expressions from a query string for display purposes
  stripGenderTagGroups(query) {
    if (!query) return '';
    let cleaned = String(query);
    // Remove grouped gender ORs like (tag:"Women" OR tag:"Ladies" ...)
    cleaned = cleaned.replace(/\(\s*tag:\"[^)]*\)\s*/ig, '');
    // Remove standalone tag:"..." tokens and surrounding AND/OR spacing
    cleaned = cleaned
      .replace(/\s*AND\s*tag:\"[^\"]+\"/ig, '')
      .replace(/\s*OR\s*tag:\"[^\"]+\"/ig, '')
      .replace(/(^|\s)tag:\"[^\"]+\"(?=\s|$)/ig, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/^AND\s+|^OR\s+/i, '')
      .replace(/\s+AND\s+$/i, '')
      .replace(/\s+OR\s+$/i, '')
      .trim();
    return cleaned;
  }

  // Client-side refinement: hide products that clearly don't match selected gender by title keywords
  applyClientSideGenderRefinement() {
    const url = new URL(window.location.href);
    const gender = (url.searchParams.get('gender') || '').toLowerCase();
    if (!gender || gender === 'all') return;

    const menKeywords = ['men', 'man', 'male', 'mens', "men's", 'boy', 'boys'];
    const womenKeywords = ['women', 'woman', 'female', 'womens', "women's", 'lady', 'ladies', 'girl', 'girls'];
    const allow = gender === 'men' ? menKeywords : womenKeywords;

    const items = document.querySelectorAll('#product-grid .grid__item');
    items.forEach((item) => {
      // Use product tags first if available
      const tagsAttr = (item.getAttribute('data-product-tags') || '').toLowerCase();
      const typeAttr = (item.getAttribute('data-product-type') || '').toLowerCase();
      const titleEl = item.querySelector('.card__heading, .full-unstyled-link, .card__information a');
      const title = (titleEl?.textContent || '').toLowerCase();

      const haystacks = [tagsAttr, typeAttr, title];

      // If any haystack includes explicit chosen gender keyword, keep
      const matchesChosen = allow.some((k) => haystacks.some(h => h.includes(k)));
      // If any haystack includes explicit opposite keyword, hide
      const opposite = gender === 'men' ? womenKeywords : menKeywords;
      const matchesOpposite = opposite.some((k) => haystacks.some(h => h.includes(k)));

      if (matchesOpposite && !matchesChosen) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });
  }

  escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Search page helper: if the query contains an exact, case-sensitive color token (e.g. "white shirt"),
   * auto-select the matching Color facet value by updating the URL filter params.
   *
   * Notes:
   * - Case-sensitive match on the facet label (so "white" != "White").
   * - Whole-token matching: avoids partial matches (e.g. "red" won't match "redwood").
   * - If a Color facet is already selected, do nothing.
   */
  autoApplyColorFacetFromQuery() {
    try {
      if (!document.body.classList.contains('template-search')) return;

      const url = new URL(window.location.href);
      const qRaw = url.searchParams.get('q') || '';
      const qBase = this.stripGenderTagGroups(qRaw).trim();
      if (!qBase) return;

      // Find the Color facet group (desktop + mobile).
      const facetRoots = [
        document.getElementById('FacetFiltersForm'),
        document.getElementById('FacetFiltersFormMobile'),
        document.getElementById('FacetFiltersPillsForm'),
      ].filter(Boolean);

      const colorDetails = [];
      facetRoots.forEach((root) => {
        root.querySelectorAll('details.js-filter').forEach((details) => {
          const label = details.querySelector('.facets__summary-label')?.textContent?.trim();
          if (!label) return;
          const labelLower = label.toLowerCase();
          if (labelLower === 'color' || labelLower === 'colour' || labelLower.includes('color') || labelLower.includes('colour')) {
            colorDetails.push(details);
          }
        });
      });
      if (!colorDetails.length) return;

      // If any Color checkbox is already selected, don't auto-change.
      if (colorDetails.some((d) => d.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked'))) return;

      // Collect candidate inputs (prefer enabled ones).
      const candidates = [];
      colorDetails.forEach((details) => {
        details
          .querySelectorAll('input[type="checkbox"][name][value], input[type="radio"][name][value]')
          .forEach((input) => {
            if (input.disabled) return;
            const id = input.getAttribute('id');
            if (!id) return;
            const labelEl = details.querySelector(`label[for="${CSS.escape(id)}"]`);
            const display =
              labelEl?.getAttribute('title') ||
              labelEl?.querySelector('.facet-checkbox__text-label')?.textContent ||
              labelEl?.textContent ||
              '';
            const displayTrimmed = String(display).trim();
            if (!displayTrimmed) return;
            candidates.push({ input, display: displayTrimmed });
          });
      });
      if (!candidates.length) return;

      const boundary = `[\\s"'()\\[\\]{}<>.,:;!?/\\\\|-]`;
      let best = null;

      candidates.forEach(({ input, display }) => {
        const escaped = this.escapeRegExp(display);
        const re = new RegExp(`(^|${boundary})${escaped}($|${boundary})`);
        const m = re.exec(qBase);
        if (!m) return;
        const idx = m.index + (m[1]?.length || 0);

        if (!best) {
          best = { input, display, idx };
          return;
        }

        // Prefer earliest match in query; if tied, prefer longer display (e.g. "off white" over "white").
        if (idx < best.idx || (idx === best.idx && display.length > best.display.length)) {
          best = { input, display, idx };
        }
      });

      if (!best) return;

      // If the param is already present in the URL, don't change.
      const existing = url.searchParams.getAll(best.input.name);
      if (existing.includes(best.input.value)) return;

      url.searchParams.append(best.input.name, best.input.value);
      window.location.replace(`${url.pathname}?${url.searchParams.toString()}`);
    } catch (_) {
      // Don't block search page if anything unexpected happens.
    }
  }
}

customElements.define('main-search', MainSearch);
